import React from 'react'
import { Form, Row, Col } from 'react-bootstrap'
import { snakeCase } from 'lodash'
import { nullish, getDefaultRelationType } from '../ArticleParse'
import type { Article } from '../types'
import type { RelationTypeValue } from '../../common/types'

type FormArticle = {
  relationType?: RelationTypeValue | null
  parentId?: string | null
}

type ParentTargetArticle = {
  id?: string | number
  optionName: string
}

type Errors = {
  parentId?: string
  relationType?: string
}

type Touched = {
  parentId?: boolean
  relationType?: boolean
}

type Values = {
  parentId?: string | null
  relationType?: RelationTypeValue | null
  article?: FormArticle | null
}

type EnumValues = {
  relationTypes: Record<string, string>
}

type Props = {
  values: Values
  errors: Errors
  touched: Touched
  setFieldValue: (field: string, value: unknown) => void
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  enumValues: EnumValues
  parentTargetArticles: ParentTargetArticle[] | undefined
  selectedArticleId: string | number | undefined
  childrenArticles: Record<string, Article[]>
}

export const RelationFields = ({
  values,
  errors,
  touched,
  setFieldValue,
  handleChange,
  handleBlur,
  enumValues,
  parentTargetArticles,
  selectedArticleId,
  childrenArticles
}: Props) => {
  return (
    <Row className='mb-2'>
      <Form.Group as={Col}>
        <Form.Label column>親記事</Form.Label>
        <Col>
          <Form.Select
            name='parentId'
            size='sm'
            value={values.parentId ?? ''}
            onChange={(e) => {
              const parentId = (e.target as HTMLSelectElement).value
              if (values.article) {
                const relationType =
                  parentId !== ''
                    ? values.article?.relationType ||
                      getDefaultRelationType(parentId, selectedArticleId, childrenArticles)
                    : ''
                const newArticle = {
                  ...values.article,
                  parentId,
                  relationType
                }
                setFieldValue('relationType', relationType)
                setFieldValue('article', newArticle)
              }
              handleChange(e)
            }}
            onBlur={handleBlur}
            isInvalid={!!(touched.parentId && errors.parentId)}
          >
            <option key='empty' value='' />
            {parentTargetArticles?.map((a) => (
              <option key={`parentId-option-${a.id}`} value={a.id}>
                {a.optionName}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type='invalid'>{errors.parentId}</Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Col}>
        <Form.Label column>関連種別</Form.Label>
        <Col>
          {!nullish(values.parentId) && (
            <>
              <Form.Select
                name='relationType'
                size='sm'
                value={values.relationType ?? ''}
                onChange={(e) => {
                  const relationType = (e.target as HTMLSelectElement).value as RelationTypeValue
                  if (values.article) {
                    const newArticle = {
                      ...values.article,
                      relationType
                    }
                    setFieldValue('article', newArticle)
                  }
                  handleChange(e)
                }}
                onBlur={handleBlur}
                disabled={nullish(values.parentId)}
                isInvalid={!!(touched.relationType && errors.relationType)}
              >
                {Object.keys(enumValues.relationTypes).map((k) => (
                  <option key={k} value={snakeCase(k)}>
                    {enumValues.relationTypes[k]}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type='invalid'>{errors.relationType}</Form.Control.Feedback>
            </>
          )}
          {nullish(values.parentId) && <Form.Control readOnly size='sm' />}
        </Col>
      </Form.Group>
    </Row>
  )
}
